import { Star } from "lucide-react";
import { PersonAvatar } from "@/components/persons/PersonAvatar";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { OriginLink } from "@/components/shared/origin-link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type RoleTeam, useSetRoleTeamLead } from "@/hooks/use-organization";

/**
 * The full membership of one role team, opened from the Team button on the org
 * structure tree.
 *
 * This replaced the inline avatar stack on the team card, so it carries the
 * navigation that stack used to provide: every member row still links to that
 * person's profile. Admins additionally get a radio column to name the team
 * lead.
 *
 * "Only one lead" needs no client-side policing — `role_team.lead_person_id` is
 * a single column, so the server simply overwrites it.
 */
export function RoleTeamDialog({
  team,
  isAdmin,
  onClose,
}: {
  team: RoleTeam | null;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const setLead = useSetRoleTeamLead();

  return (
    <Dialog open={team !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{team?.name ?? "Team"}</DialogTitle>
          <DialogDescription>
            {team === null
              ? null
              : team.people.length === 0
                ? "No one is assigned to this team yet."
                : isAdmin
                  ? `${team.people.length} ${team.people.length === 1 ? "person" : "people"} — select one as the team lead.`
                  : `${team.people.length} ${team.people.length === 1 ? "person" : "people"} on this team.`}
          </DialogDescription>
        </DialogHeader>

        {team !== null && team.people.length > 0 && (
          <div className="-mx-2 max-h-[55vh] overflow-y-auto px-2">
            <ul className="divide-y">
              {team.people.map((person) => {
                const isLead = team.leadPersonId === person.id;
                return (
                  <li key={person.id} className="flex items-center gap-3 py-2">
                    {isAdmin ? (
                      // A real radio, not a styled button: one native control
                      // per group gives keyboard/AT behaviour for free, and
                      // biome rejects role="radio" on a <button>. Selecting is
                      // onChange; clearing is the separate action below, since
                      // a radio cannot fire onChange to un-check itself.
                      <input
                        type="radio"
                        name={`team-lead-${team.id}`}
                        checked={isLead}
                        disabled={setLead.isPending}
                        aria-label={`Make ${person.name} the team lead`}
                        onChange={() =>
                          setLead.mutate({ roleTeamId: team.id, leadPersonId: person.id })
                        }
                        className="size-4 shrink-0 accent-primary"
                      />
                    ) : (
                      // Non-admins see the outcome, not a control.
                      <span className="flex size-4 shrink-0 items-center justify-center">
                        {isLead && <Star className="size-4 fill-current text-primary" />}
                      </span>
                    )}

                    <OriginLink
                      to="/dashboard/persons/$personId"
                      params={{ personId: person.id }}
                      className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-0.5 transition-colors hover:text-primary"
                    >
                      <PersonAvatar
                        name={person.name}
                        seed={person.id}
                        size="sm"
                        imageUrl={person.avatarUrl}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-sm">{person.name}</span>
                        {person.title && (
                          <span className="block truncate text-muted-foreground text-xs">
                            {person.title}
                          </span>
                        )}
                      </span>
                    </OriginLink>

                    {isLead && (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-2xs text-primary">
                        Team Lead
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          {isAdmin && team !== null && team.leadPersonId !== null ? (
            <ConfirmDialog
              title="Remove the team lead?"
              description="The team is left with no lead, and the badge disappears from that person's profile."
              confirmLabel="Remove team lead"
              destructive
              onConfirm={() => setLead.mutate({ roleTeamId: team.id, leadPersonId: null })}
              trigger={
                <Button variant="ghost" size="sm" disabled={setLead.isPending}>
                  Remove team lead
                </Button>
              }
            />
          ) : (
            <span />
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
